import { useNavigate } from 'react-router-dom';
import { ExternalLink, ArrowRight } from '../../icons/wp-icons';

const ICONS = {
  customer: '👤',
  invoice: '📄',
  quote: '📋',
  project: '🏗️',
  appointment: '📅',
  request: '📨',
  catalog: '📦',
  calendar: '📅',
  default: '🔗'
};

export function ChatActions({ data, onAction, onQuickReply, onSignature }) {
  const navigate = useNavigate();

  const handleLinkClick = (url) => {
    // Interne Links: Navigation innerhalb der App
    if (url.startsWith('/')) {
      navigate(url);
    } else {
      // Externe Links: In neuem Tab öffnen
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="chat-actions-container">
      {/* Text-Inhalt */}
      {data.text && (
        <p className="chat-actions-text whitespace-pre-wrap">{data.text}</p>
      )}

      {/* A) Links zu Objekten */}
      {data.links?.length > 0 && (
        <div className="chat-action-links">
          {data.links.map((link, i) => (
            <button
              key={i}
              onClick={() => handleLinkClick(link.url)}
              className="chat-link-button"
              title={link.url}
            >
              <span className="chat-link-icon">
                {ICONS[link.icon] || ICONS.default}
              </span>
              <span className="chat-link-label">{link.label}</span>
              {!link.url.startsWith('/') && (
                <ExternalLink className="w-3 h-3 opacity-50" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* B) Action-Buttons */}
      {data.actions?.length > 0 && (
        <div className="chat-action-buttons">
          {data.actions.map((action, i) => (
            <button
              key={i}
              onClick={() => {
                if (action.confirm && !window.confirm(action.confirm)) return;
                onAction(action.action, action.params);
              }}
              className={`chat-action-btn chat-action-btn-${action.variant || 'secondary'}`}
            >
              {action.label}
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </button>
          ))}
        </div>
      )}

      {/* C) Quick-Reply Buttons */}
      {data.quickReplies?.length > 0 && (
        <div className="chat-quick-replies">
          {data.quickReplies.map((qr, i) => (
            <button
              key={i}
              onClick={() => qr.signatureAction && onSignature ? onSignature(qr.signatureAction) : onQuickReply(qr.message)}
              className="chat-quick-reply-btn"
            >
              {qr.label}
            </button>
          ))}
        </div>
      )}

      {/* Styling inline via style tag for component encapsulation */}
      <style>{`
        .chat-actions-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .chat-actions-text {
          margin: 0;
          line-height: 1.5;
        }

        .chat-action-links {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .chat-link-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          font-size: 15px;
          color: #374151;
          transition: all 0.15s ease;
          min-height: 44px;
        }

        .chat-link-button:hover {
          background: #e5e7eb;
          border-color: #d1d5db;
          transform: translateY(-1px);
        }

        .chat-link-button:active {
          transform: translateY(0);
        }

        .chat-link-icon {
          font-size: 16px;
        }

        .chat-link-label {
          font-weight: 500;
        }

        .chat-action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .chat-action-btn {
          display: inline-flex;
          align-items: center;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          min-height: 44px;
        }

        .chat-action-btn-primary {
          background: #059669;
          color: white;
          border: none;
          font-weight: 600;
        }

        .chat-action-btn-primary:hover {
          background: #059669;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .chat-action-btn-secondary {
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .chat-action-btn-secondary:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .chat-action-btn-danger {
          background: #ef4444;
          color: white;
          border: none;
        }

        .chat-action-btn-danger:hover {
          background: #dc2626;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
        }

        .chat-quick-replies {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 4px;
        }

        .chat-quick-reply-btn {
          padding: 10px 16px;
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #93c5fd;
          border-radius: 16px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          min-height: 44px;
        }

        .chat-quick-reply-btn:hover {
          background: #bfdbfe;
          border-color: #60a5fa;
        }

        /* Mobile-spezifische Anpassungen für bessere Touch-Bedienung */
        @media (max-width: 768px) {
          .chat-action-btn,
          .chat-quick-reply-btn,
          .chat-link-button {
            padding: 14px 20px;
            font-size: 17px;
            min-height: 48px;
          }

          .chat-action-buttons,
          .chat-quick-replies,
          .chat-action-links {
            flex-direction: column;
            gap: 10px;
          }

          .chat-action-btn,
          .chat-quick-reply-btn,
          .chat-link-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default ChatActions;
